import { z } from 'zod';
import { Id, IsoDate, PaginationQuery, paginated } from './common';

/**
 * Coûts (`data-model.md` § `cost_ledger`, `api.md` § Configuration et gouvernance) : résumé du
 * mois (`GET /costs/summary`) et ledger paginé (`GET /costs/ledger`).
 */

export const COST_PROVIDERS = ['google_ads', 'dataforseo', 'openai', 'workers_ai'] as const;
export const CostProvider = z.enum(COST_PROVIDERS);
export type CostProvider = z.infer<typeof CostProvider>;

export const YEAR_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
/** `YYYY-MM`. */
export const YearMonth = z.string().regex(YEAR_MONTH_PATTERN, 'validation.month');
export type YearMonth = z.infer<typeof YearMonth>;

/** `?month=YYYY-MM` ; mois courant (UTC) par défaut. */
export const CostsSummaryQuery = z.object({ month: YearMonth.optional() });
export type CostsSummaryQuery = z.infer<typeof CostsSummaryQuery>;

export const CostsProviderTotal = z.object({
  provider: CostProvider,
  costUsd: z.number(),
  /** Somme des `units` (Google Ads : opérations de quota, pas de coût en $). */
  units: z.number().int(),
  entries: z.number().int(),
});
export type CostsProviderTotal = z.infer<typeof CostsProviderTotal>;

export const CostsScanTotal = z.object({
  /** `null` = lignes hors scan (rafraîchissements, ré-analyses). */
  scanId: z.string().nullable(),
  label: z.string().nullable(),
  marketId: z.string().nullable(),
  costUsd: z.number(),
  /** Part du total du mois (0–1). */
  share: z.number(),
});
export type CostsScanTotal = z.infer<typeof CostsScanTotal>;

export const CostsSummary = z.object({
  month: YearMonth,
  spentUsd: z.number(),
  capUsd: z.number(),
  warnRatio: z.number(),
  /** `spentUsd / capUsd` (0 si le plafond est nul). */
  ratio: z.number(),
  byProvider: z.array(CostsProviderTotal),
  byScan: z.array(CostsScanTotal),
});
export type CostsSummary = z.infer<typeof CostsSummary>;

const DateInput = z.union([z.iso.date(), z.iso.datetime({ offset: true })]);

/** `GET /costs/ledger` : filtres `provider`, `scanId`, `from`, `to` (date ou date-heure ISO). */
export const CostsLedgerQuery = PaginationQuery.extend({
  provider: CostProvider.optional(),
  scanId: z.string().min(1).optional(),
  from: DateInput.optional(),
  to: DateInput.optional(),
});
export type CostsLedgerQuery = z.infer<typeof CostsLedgerQuery>;

export const CostLedgerRow = z.object({
  id: z.number().int(),
  provider: CostProvider,
  operation: z.string(),
  units: z.number().int(),
  unitType: z.string(),
  costUsd: z.number(),
  scanId: Id.nullable(),
  scanLabel: z.string().nullable(),
  clusterId: Id.nullable(),
  opportunityId: Id.nullable(),
  createdAt: IsoDate,
});
export type CostLedgerRow = z.infer<typeof CostLedgerRow>;

export const CostsLedgerResponse = paginated(CostLedgerRow);
export type CostsLedgerResponse = z.infer<typeof CostsLedgerResponse>;
